import {
  createMembershipGrid,
  createVenn,
  type VennConfig,
  type MembershipGridHandle,
  type VennHandle,
} from '@learnforge/demo-render';
import {
  defineExperiment,
  type CustomExperimentSpec,
  type PanelRow,
} from '@learnforge/demo-render/harness';
import type { SetsContext, SetsKey } from '../types';
import {
  namedPredicate,
  union,
  intersect,
  difference,
  symmetricDifference,
  complement,
  cardinality,
  classifyPair,
  inclusionExclusion,
} from '../sets';

// ── State ───────────────────────────────────────────────────────────────────

export interface State {
  universe: number[];
  predicateA: string;
  predicateB: string;
  predicateC: string | null;
  op: 'union' | 'intersect' | 'difference' | 'symDiff' | 'complement';
  advanced: boolean;
}

// ── Available predicates (id → i18n key) ───────────────────────────────────

const PREDICATES: { value: string; labelKey: SetsKey }[] = [
  { value: 'even', labelKey: 'set.even' },
  { value: 'odd', labelKey: 'set.odd' },
  { value: 'prime', labelKey: 'set.prime' },
  { value: 'multipleOf3', labelKey: 'set.multipleOf3' },
  { value: 'multipleOf5', labelKey: 'set.multipleOf5' },
  { value: 'gt10', labelKey: 'set.gt10' },
  { value: 'lt5', labelKey: 'set.lt5' },
];

// Available ops (basic and advanced)
const OPS_BASIC: { value: State['op']; labelKey: SetsKey }[] = [
  { value: 'intersect', labelKey: 'op.intersect' },
  { value: 'union', labelKey: 'op.union' },
  { value: 'difference', labelKey: 'op.difference' },
  { value: 'complement', labelKey: 'op.complement' },
];

const OPS_ADVANCED: { value: State['op']; labelKey: SetsKey }[] = [
  ...OPS_BASIC,
  { value: 'symDiff', labelKey: 'op.symDiff' },
];

// ── Pure logic ──────────────────────────────────────────────────────────────

/**
 * Compute the result set given the current state.
 * Exported for unit-testability (DOM-free).
 */
export function resultSet(state: State): number[] {
  const { universe, predicateA, predicateB, predicateC, op, advanced } = state;

  const fnA = namedPredicate(predicateA);
  const fnB = namedPredicate(predicateB);

  const setA = universe.filter(fnA).map(String);
  const setB = universe.filter(fnB).map(String);
  const universeStr = universe.map(String);

  // When advanced AND there is a C predicate, extend the binary op to three sets
  let resultStr: string[];
  if (advanced && predicateC !== null) {
    const fnC = namedPredicate(predicateC);
    const setC = universe.filter(fnC).map(String);
    switch (op) {
      case 'intersect':
        resultStr = intersect(intersect(setA, setB), setC);
        break;
      case 'union':
        resultStr = union(union(setA, setB), setC);
        break;
      case 'difference':
        // A \ B \ C
        resultStr = difference(difference(setA, setB), setC);
        break;
      case 'symDiff':
        // (A △ B) △ C
        resultStr = symmetricDifference(symmetricDifference(setA, setB), setC);
        break;
      case 'complement':
        // complement of A in universe (C ignored for complement)
        resultStr = complement(universeStr, setA);
        break;
      default:
        resultStr = [];
    }
  } else {
    switch (op) {
      case 'union':
        resultStr = union(setA, setB);
        break;
      case 'intersect':
        resultStr = intersect(setA, setB);
        break;
      case 'difference':
        resultStr = difference(setA, setB);
        break;
      case 'symDiff':
        resultStr = symmetricDifference(setA, setB);
        break;
      case 'complement':
        resultStr = complement(universeStr, setA);
        break;
      default:
        resultStr = [];
    }
  }

  // Convert back to numbers in canonical numeric order
  return resultStr.map(Number).sort((a, b) => a - b);
}

// ── Spec ────────────────────────────────────────────────────────────────────

const UNIVERSE_1_20 = Array.from({ length: 20 }, (_, i) => i + 1);

function buildSpec(): CustomExperimentSpec<State, SetsKey, SetsContext> {
  return {
    labelKey: 'experiment.set-builder-lab.label',
    demoId: 'sets',
    advancedKey: 'set-builder-lab',

    initialState(ctx): State {
      return {
        universe: UNIVERSE_1_20,
        predicateA: 'even',
        predicateB: 'multipleOf3',
        predicateC: ctx.advanced ? 'prime' : null,
        op: 'intersect',
        advanced: ctx.advanced,
      };
    },

    panel(state, ctx): PanelRow<SetsKey, State>[] {
      const advanced = ctx.advanced;
      const ops = advanced ? OPS_ADVANCED : OPS_BASIC;
      const rows: PanelRow<SetsKey, State>[] = [
        {
          kind: 'dropdown',
          id: 'predicateA',
          labelKey: 'param.predicateA',
          options: PREDICATES,
          value: state.predicateA,
          onChange: (v) => ({ ...state, predicateA: v }),
        },
        {
          kind: 'dropdown',
          id: 'predicateB',
          labelKey: 'param.predicateB',
          options: PREDICATES,
          value: state.predicateB,
          onChange: (v) => ({ ...state, predicateB: v }),
        },
        {
          kind: 'dropdown',
          id: 'op',
          labelKey: 'param.op',
          options: ops,
          value: state.op,
          onChange: (v) => ({ ...state, op: v as State['op'] }),
        },
        {
          kind: 'advanced-toggle',
          labelKey: 'btn.advanced',
        },
      ];

      if (advanced) {
        // Insert C predicate dropdown before the advanced toggle
        rows.splice(rows.length - 1, 0, {
          kind: 'dropdown',
          id: 'predicateC',
          labelKey: 'param.predicateC',
          options: PREDICATES,
          value: state.predicateC ?? 'prime',
          onChange: (v) => ({ ...state, predicateC: v }),
        });
      }

      return rows;
    },

    createRender(ctx, host, _controls) {
      // ── Container ──
      const container = document.createElement('div');
      container.className = 'set-builder-lab';
      host.appendChild(container);

      // Placeholder handles — initialised in first update()
      let gridHandle: MembershipGridHandle | null = null;
      let vennHandle: VennHandle | null = null;
      let vennContainer: HTMLDivElement | null = null;
      let lastRelation: string | null = null;

      // ── Roster + cardinality readout ──
      const rosterEl = document.createElement('div');
      rosterEl.className = 'set-builder-roster';
      container.appendChild(rosterEl);

      // ── Inclusion-exclusion readout (advanced only) ──
      const ieEl = document.createElement('div');
      ieEl.className = 'set-builder-ie';
      container.appendChild(ieEl);

      function buildVennConfig(setA: string[], setB: string[], state: State): VennConfig {
        const setASet = new Set(setA);
        const setBSet = new Set(setB);

        // Region counts: A-only, B-only, AB intersection, universe outside both
        const ab = setA.filter((x) => setBSet.has(x));
        const aOnly = setA.filter((x) => !setBSet.has(x));
        const bOnly = setB.filter((x) => !setASet.has(x));
        const universeStr = state.universe.map(String);
        const inEither = new Set([...setA, ...setB]);
        const noneCount = universeStr.filter((x) => !inEither.has(x)).length;

        return {
          sets: [
            { id: 'A', labelKey: 'param.predicateA', cx: 0, cy: 0, r: 100 },
            { id: 'B', labelKey: 'param.predicateB', cx: 0, cy: 0, r: 100 },
          ],
          universe: state.universe.length,
          regionCounts: {
            A: aOnly.length,
            B: bOnly.length,
            AB: ab.length,
            none: noneCount,
          },
        };
      }

      function update(state: State): void {
        const advanced = ctx.advanced;
        const result = resultSet({ ...state, advanced });
        const resultStr = result.map(String);

        const fnA = namedPredicate(state.predicateA);
        const fnB = namedPredicate(state.predicateB);
        const setA = state.universe.filter(fnA).map(String);
        const setB = state.universe.filter(fnB).map(String);
        const universeStr = state.universe.map(String);

        // ── Membership grid ──
        const columns: { id: string; label: string }[] = [
          { id: 'A', label: ctx.i18n.t('param.predicateA') },
          { id: 'B', label: ctx.i18n.t('param.predicateB') },
          { id: 'result', label: ctx.i18n.t('stat.result') },
        ];

        const setASet = new Set(setA);
        const setBSet = new Set(setB);
        const resultStrSet = new Set(resultStr);

        const gridOpts = {
          universe: universeStr,
          columns,
          isMember(i: number, colId: string): boolean {
            const el = universeStr[i] ?? '';
            switch (colId) {
              case 'A':
                return setASet.has(el);
              case 'B':
                return setBSet.has(el);
              case 'result':
                return resultStrSet.has(el);
              default:
                return false;
            }
          },
          highlightColumn: 'result' as string | null,
        };

        if (gridHandle === null) {
          // First render: create
          const gridContainer = document.createElement('div');
          gridContainer.className = 'set-builder-grid';
          container.insertBefore(gridContainer, rosterEl);
          gridHandle = createMembershipGrid(gridContainer, gridOpts);
        } else {
          gridHandle.update(gridOpts);
        }

        // ── Venn diagram ──
        // Adaptive layout: recreate when the relation changes (the relation is
        // captured in opts at createVenn() time and cannot be changed afterward).
        const relation = classifyPair(setA, setB);
        const vennConfig = buildVennConfig(setA, setB, state);

        if (vennHandle === null || lastRelation !== relation) {
          // Destroy old handle if it exists, then recreate
          vennHandle?.destroy();
          if (vennContainer === null) {
            vennContainer = document.createElement('div');
            vennContainer.className = 'set-builder-venn';
            container.insertBefore(vennContainer, rosterEl);
          } else {
            // Clear the container so createVenn can append a fresh SVG
            vennContainer.innerHTML = '';
          }
          vennHandle = createVenn({
            container: vennContainer,
            i18n: ctx.i18n,
            initial: vennConfig,
            layout: { mode: 'adaptive', relation },
          });
          lastRelation = relation;
        } else {
          vennHandle.update(vennConfig);
        }

        // ── Roster + cardinality ──
        const card = cardinality(resultStr);
        rosterEl.textContent = '';
        const rosterTitle = document.createElement('span');
        rosterTitle.className = 'set-builder-result-label';
        rosterTitle.textContent = `${ctx.i18n.t('stat.result')}: {${result.join(', ')}}`;
        rosterEl.appendChild(rosterTitle);
        const cardEl = document.createElement('span');
        cardEl.className = 'set-builder-cardinality';
        cardEl.textContent = ` ${ctx.i18n.t('stat.cardinality')}: ${card}`;
        rosterEl.appendChild(cardEl);

        // ── Inclusion-exclusion readout (advanced) ──
        ieEl.textContent = '';
        if (advanced) {
          const sets: string[][] = [setA, setB];
          if (state.predicateC !== null) {
            const fnC = namedPredicate(state.predicateC);
            sets.push(state.universe.filter(fnC).map(String));
          }
          const ie = inclusionExclusion(sets);
          const ieRow = document.createElement('div');
          ieRow.className = 'set-builder-ie-row';
          ieRow.textContent = `${ctx.i18n.t('stat.inclusionExclusion')}: |A∪…| = ${ie.total}`;
          ieEl.appendChild(ieRow);
        }
      }

      return {
        update,
        dispose() {
          gridHandle?.destroy();
          gridHandle = null;
          vennHandle?.destroy();
          vennHandle = null;
          vennContainer = null;
          lastRelation = null;
        },
      };
    },

    actions(state, ctx) {
      return {
        onReset() {
          return {
            universe: UNIVERSE_1_20,
            predicateA: 'even',
            predicateB: 'multipleOf3',
            predicateC: ctx.advanced ? 'prime' : null,
            op: 'intersect' as const,
            advanced: ctx.advanced,
          };
        },
        onRandomise() {
          const predicateIds = PREDICATES.map((p) => p.value);
          const ops: State['op'][] = ctx.advanced
            ? ['union', 'intersect', 'difference', 'symDiff', 'complement']
            : ['union', 'intersect', 'difference', 'complement'];
          const pA = predicateIds[Math.floor(ctx.prng() * predicateIds.length)] ?? 'even';
          let pB = predicateIds[Math.floor(ctx.prng() * predicateIds.length)] ?? 'odd';
          // Avoid same predicate for A and B
          if (pB === pA) {
            pB = predicateIds[(predicateIds.indexOf(pA) + 1) % predicateIds.length] ?? 'odd';
          }
          const op = ops[Math.floor(ctx.prng() * ops.length)] ?? 'intersect';
          return {
            ...state,
            predicateA: pA,
            predicateB: pB,
            predicateC: ctx.advanced
              ? (predicateIds[Math.floor(ctx.prng() * predicateIds.length)] ?? 'prime')
              : null,
            op,
          };
        },
        presets: [
          { id: 'even-odd', labelKey: 'preset.evenOdd' as SetsKey },
          { id: 'prime-mult3', labelKey: 'preset.primeMult3' as SetsKey },
        ],
        onPreset(s: State, id: string) {
          switch (id) {
            case 'even-odd':
              return { ...s, predicateA: 'even', predicateB: 'odd', op: 'union' as const };
            case 'prime-mult3':
              return {
                ...s,
                predicateA: 'prime',
                predicateB: 'multipleOf3',
                op: 'intersect' as const,
              };
            default:
              return s;
          }
        },
      };
    },

    applyShotState(state): State {
      // §10 shot pose: A=even, B=multipleOf3, op=intersect, advanced off
      return {
        ...state,
        predicateA: 'even',
        predicateB: 'multipleOf3',
        predicateC: null,
        op: 'intersect',
        advanced: false,
      };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
